// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld/base/core';
import 'myWorld/base/control';
import 'myWorld/uiComponents/index';

/**
 * Options for FieldEditor instances
 * @typedef fieldEditorOptions
 * @property {boolean}  [expandedMode=false]    Whether the input should use more space if useful (ex: button instead of dropdown)<br/>
 *                                               or if it should remain compact
 */

myw.FieldEditor = myw.Control.extend({
    options: {
        expandedMode: false
    },

    /**
     * @class An input element apropriate to edit the value of a given feature's field <br/>
     *        Super-class for: <br/>
     *        {@link BooleanFieldEditor} <br/>
     *        {@link DateFieldEditor} <br/>
     *        {@link LinkFieldEditor} <br/>
     *        {@link NumberFieldEditor} <br/>
     *        {@link ReferenceFieldEditor} <br/>
     *        {@link StringFieldEditor} <br/>
     *        {@link TimeFieldEditor} <br/>
     * @param  {DDFeature}          feature
     * @param  {fieldDD}                fieldDD     Data dictionary information of the field to edit
     * @param  {fieldEditorOptions}     options
     * @constructs
     */
    initialize(owner, feature, fieldDD, options) {
        myw.Control.prototype.initialize.call(this, owner, options);

        this.feature = feature;
        this.fieldDD = fieldDD;
        this.fieldValue = this.convertValueForDisplay(
            this.feature.getProperties()[fieldDD.internal_name]
        );
        const isKeyField = fieldDD.internal_name === feature.keyFieldName;
        this.required = (fieldDD.hasOwnProperty('mandatory') && fieldDD.mandatory) || isKeyField;

        this.render();
    },

    /**
     * Updates the UI to match the current value
     * Override in subclasses
     * Called when the value is modified with setValue()
     */
    render() {},

    /**
     * Returns the given value <br/>
     * Override to converts a value to its apropriate representation on the screen
     * Example: by using the features displayValues information
     * @param  fieldValue
     */
    convertValueForDisplay(fieldValue) {
        return fieldValue;
    },

    /**
     * Returns the value currently set in the UI
     */
    getValue() {
        if (this.control) {
            return this.control.getValue();
        }
        return this.$el.val();
    },

    /**
     * Sets(changes) the current value
     * @param value new value
     */
    setValue(value) {
        if (this.control) {
            this.control.setValue(value);
            return;
        }
        this.fieldValue = value;
        this.render();
    },

    /**
     * Called when the UI informs that the value as been changed by the user
     * @param  ev [description]
     * @private
     */
    _changed(ev) {
        ev = ev || {};
        ev.fieldName = this.fieldDD.internal_name;
        this.trigger('change', ev);
    },

    /**
     * Checks if the provided value is valid. <br/>
     * Responsible for mandatory value check. Remaining checks delegated to validateValue()
     * @param  value Value to check
     * @return {boolean|string}       True if the value is valid or an error message if not
     */
    validate(value) {
        const missing =
            typeof value === 'undefined' || value === null || value.toString().length === 0;

        if (this.required && missing) {
            return this.msg('value_required');
        } else if (missing) {
            // a null value is always valid for a non-mandatory field
            return true;
        } else {
            return this.validateValue(value);
        }
    },

    /**
     * Returns true<br/>
     * Override with the logic to check if the provided value is valid
     * @param  value Value to check
     * @return {boolean}       Whether the value is valid or not
     */
    validateValue(value) {
        return true;
    }
});

export default myw.FieldEditor;
